/**
 * salesDataSource.ts — WatermelonDB → SalesSummaryInput bridge.
 *
 * Note on the payment query: `payments` carries no `store_id`, so it cannot be
 * scoped directly. We select payments by `received_at` (the cash basis — when
 * the money actually arrived), then resolve each one's parent invoice to filter
 * by store and to learn its sign. The old code selected payments by the
 * invoice IDs raised in the range, which meant a credit sale raised on 1 July
 * and settled on 15 July was reported as *cash taken on 1 July* — so last
 * month's report changed every time you re-ran it.
 */

import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { fetchInChunks } from '@/db/fetchInChunks';
import { Invoice, LineItem, Payment, VOIDED_INVOICE_STATUSES } from '@/db/models/Billing';
import { Product } from '@/db/models/Inventory';
import type { SalesSummaryInput } from './salesSummary';

/** Load a store's sales activity for the half-open range `[from, to)`. */
export async function loadSalesRange(
  storeId: string,
  from: Date,
  to: Date,
): Promise<SalesSummaryInput> {
  const fromMs = from.getTime();
  const toMs = to.getTime();

  const invoices = await database.get<Invoice>('invoices').query(
    Q.where('store_id', storeId),
    Q.where('is_deleted', false),
    Q.where('status', Q.notIn(VOIDED_INVOICE_STATUSES)),
    Q.where('type', Q.oneOf(['SALE', 'CREDIT_NOTE'])),
    Q.where('created_at', Q.gte(fromMs)),
    Q.where('created_at', Q.lt(toMs)),
  ).fetch();

  const invoiceIds = invoices.map((i) => i.id);
  const typeByInvoiceId = new Map(invoices.map((i) => [i.id, i.type]));

  // ── Line items for the invoices raised in the range (accrual basis) ──
  const lineRows = await fetchInChunks(invoiceIds, (batch) =>
    database.get<LineItem>('line_items').query(
      Q.where('invoice_id', Q.oneOf(batch)),
      Q.where('is_deleted', false),
    ).fetch(),
  );

  // ── Payments received in the range (cash basis) ──
  // Selected by date, then filtered to this store via the parent invoice.
  const paymentRows = await database.get<Payment>('payments').query(
    Q.where('is_deleted', false),
    Q.where('received_at', Q.gte(fromMs)),
    Q.where('received_at', Q.lt(toMs)),
  ).fetch();

  // Parent invoices of those payments may fall outside the range (a July
  // payment against a June invoice), so resolve any we haven't already loaded.
  const unknownParentIds = [
    ...new Set(paymentRows.map((p) => p.invoiceId).filter((id) => !typeByInvoiceId.has(id))),
  ];
  const parentInvoices = await fetchInChunks(unknownParentIds, (batch) =>
    database.get<Invoice>('invoices').query(
      Q.where('id', Q.oneOf(batch)),
      Q.where('store_id', storeId),
      Q.where('is_deleted', false),
      Q.where('status', Q.notIn(VOIDED_INVOICE_STATUSES)),
    ).fetch(),
  );
  const parentType = new Map(typeByInvoiceId);
  for (const inv of parentInvoices) parentType.set(inv.id, inv.type);

  // ── Product names for the top-products table ──
  const productIds = [...new Set(lineRows.map((li) => li.productId))];
  const products = await fetchInChunks(productIds, (batch) =>
    database.get<Product>('products').query(
      Q.where('id', Q.oneOf(batch)),
      Q.where('is_deleted', false),
    ).fetch(),
  );
  const productNames = new Map(products.map((p) => [p.id, p.name]));

  return {
    invoices: invoices.map((i) => ({
      type: i.type,
      totalAmount: i.totalAmount,
      totalTax: i.totalTax,
      dueAmount: i.dueAmount,
      createdAt: i.createdAt,
    })),
    // Drop payments whose invoice belongs to another store, was voided, or has
    // vanished — `parentType` only holds this store's live invoices.
    payments: paymentRows.flatMap((p) => {
      const invoiceType = parentType.get(p.invoiceId);
      return invoiceType ? [{ method: p.method, amount: p.amount, invoiceType }] : [];
    }),
    lines: lineRows.map((li) => ({
      productId: li.productId,
      quantity: li.quantity,
      totalAmount: li.totalAmount,
      invoiceType: typeByInvoiceId.get(li.invoiceId) ?? 'SALE',
    })),
    productNames,
  };
}
